import { z } from "zod";
import { fillProduct, PRODUCTS, Product } from "./product.js";
import { SupportFeature, supportFeatureOf } from "./supportTriage.js";
import { fillLabels, PRESET_LABELS, VOCAB_KEYS, type Labels } from "./vocabulary.js";

/**
 * ヘルプの記事（サポート基盤 S2、#150、docs/59）。ここ1か所が正本：
 *   - 記事の頭（help/content/**.md の front matter）の形と、記事の書き方の決まり（手順は5手まで など）
 *   - ビルドが出す help-index.json の形（公開の記事だけ。言葉のキーは埋めずに残す）
 *   - 「？」の窓（apps/web）の「この画面でよく見られている質問」の選び方と「ヘルプを探す」
 *   - 窓で数える 開いた・はい・いいえ（POST /help/events → 製品 DB の help_views。人も会社も持たない）
 * 記事は {customer} {project} などの言葉のキーと {pw:…} で書く。公開のヘルプは製品の既定（fillHelpText）、窓の中は会社の言葉（画面の t()）
 */

/** 公開のヘルプの住所（製品のドメインの /help/。FS の Caddy が配る） */
export const HELP_BASE = "/help";
/** ビルドが出す索引（/help/help-index.json） */
export const HELP_INDEX_FILE = "help-index.json";

export const HELP_LIMITS = {
  /** 手順（番号つきの行）は1記事に5手まで */
  steps: 5,
  /** 「この画面でよく見られている質問」に並べる数 */
  perScreen: 5,
  /** 題の長さ（利用者の言葉で短く） */
  titleMax: 40,
  /** 記事が指す画面の数 */
  screensMax: 10,
  /** 「ヘルプを探す」に並べる数・打てる長さ */
  searchMax: 10,
  queryMax: 60,
} as const;

/** 記事の ID（ファイル名と同じ。URL の /help/<id>/ になる） */
export const HelpArticleId = z
  .string()
  .max(60)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "記事の ID は半角の小文字・数字・ハイフンで");
/** 記事の製品：共通（両方のビルド）／現場力だけ（field の記事もここ）／営業力だけ */
export const HelpScope = z.enum(["common", "genba", "eigyo"]);
export type HelpScope = z.infer<typeof HelpScope>;
/** 状態：下書きは公開のヘルプ・help-index・「？」の窓に出さない（管理アプリの「ヘルプの下書き」だけ） */
export const HelpStatus = z.enum(["draft", "published"]);
export type HelpStatus = z.infer<typeof HelpStatus>;
/** 記事が指す画面（画面のパスの形。id は :id。末尾の /* は「その下の画面すべて」） */
export const HelpScreen = z
  .string()
  .max(100)
  .regex(/^\/[a-z0-9\-/:]*(?:\/\*)?$/, "画面は /estimates/:id のようなパスで");

/** 記事の頭（front matter）。知らない項目は止める */
export const HelpFrontMatter = z
  .object({
    id: HelpArticleId,
    title: z.string().trim().min(1).max(HELP_LIMITS.titleMax, `題は ${HELP_LIMITS.titleMax}字まで`),
    product: HelpScope,
    feature: SupportFeature,
    screens: z.array(HelpScreen).min(1).max(HELP_LIMITS.screensMax),
    /** 並び順（小さいほど上。「よく見られている質問」の最初の並び） */
    order: z.number().int().min(1).max(9999),
    status: HelpStatus,
    /** 最終確認日（画面と見くらべて確かめた日） */
    checked: z.iso.date(),
  })
  .strict();
export type HelpFrontMatter = z.infer<typeof HelpFrontMatter>;

/** 画面の写し（記事の1手に1枚）。phone が null＝スマホの画面が別の作り（PC の写しを縮めて見せる） */
export const HelpShot = z.object({
  alt: z.string(),
  pc: z.string(),
  phone: z.string().nullable(),
  w: z.number().int(),
  h: z.number().int(),
});
export type HelpShot = z.infer<typeof HelpShot>;

/** 見出しで区切った本文。text＝探す・AI の材料（印のない文）、md＝窓に出す形（記事の Markdown のまま。写しは shot:名前） */
export const HelpSection = z.object({
  heading: z.string().nullable(),
  text: z.string(),
  md: z.string(),
});
export type HelpSection = z.infer<typeof HelpSection>;

export const HelpIndexArticle = z.object({
  id: HelpArticleId,
  title: z.string(),
  product: HelpScope,
  feature: SupportFeature,
  screens: z.array(HelpScreen),
  order: z.number().int(),
  status: HelpStatus,
  checked: z.string(),
  /** 公開のヘルプの住所（/help/<id>/） */
  url: z.string(),
  sections: z.array(HelpSection),
  shots: z.record(z.string(), HelpShot),
});
export type HelpIndexArticle = z.infer<typeof HelpIndexArticle>;

export const HelpIndex = z.object({
  v: z.literal(1),
  product: Product,
  articles: z.array(HelpIndexArticle),
});
export type HelpIndex = z.infer<typeof HelpIndex>;

/** 画面のパス（apps/web の screenPath＝id を :id に伏せた形）が記事の画面に当たるか */
export function helpScreenMatches(pattern: string, screen: string): boolean {
  const s = (screen.split(/[?#]/)[0] ?? "").replace(/\/+$/, "") || "/";
  if (pattern.endsWith("/*")) {
    const base = pattern.slice(0, -2).replace(/\/+$/, "") || "/";
    return s === base || s.startsWith(base === "/" ? "/" : base + "/");
  }
  return s === (pattern.replace(/\/+$/, "") || "/");
}

/** その画面の公開の記事（並び順。5件まで）。下書きは index に入らないが、念のためここでも外す */
export function helpArticlesFor(articles: readonly HelpIndexArticle[], screen: string, max: number = HELP_LIMITS.perScreen): HelpIndexArticle[] {
  return articles
    .filter((a) => a.status === "published" && a.screens.some((p) => helpScreenMatches(p, screen)))
    .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id))
    .slice(0, max);
}

/** 探す文字の形をそろえる（全角英数・半角カナ → NFKC、大文字 → 小文字） */
const norm = (s: string): string => s.normalize("NFKC").toLowerCase();

export type HelpHit = { article: HelpIndexArticle; section: HelpSection | null; score: number };
/**
 * 「ヘルプを探す」：空白で区切った言葉が全部入っている記事を、題 → 見出し → 本文の順に重く数える。
 * fill＝言葉のキーを埋める関数（窓は会社の言葉＝画面の t()）。埋めてから探すので「得意先」に言い換えた会社は「得意先」で当たる
 */
export function helpSearch(articles: readonly HelpIndexArticle[], query: string, fill: (s: string) => string = (s) => s, max: number = HELP_LIMITS.searchMax): HelpHit[] {
  const words = norm(query.slice(0, HELP_LIMITS.queryMax)).split(/[\s　]+/).filter(Boolean);
  if (words.length === 0) return [];
  const hits: HelpHit[] = [];
  for (const a of articles) {
    if (a.status !== "published") continue;
    const title = norm(fill(a.title));
    const secs = a.sections.map((s) => ({ s, head: norm(fill(s.heading ?? "")), body: norm(fill(s.text)) }));
    const all = title + "\n" + secs.map((x) => x.head + "\n" + x.body).join("\n");
    if (!words.every((w) => all.includes(w))) continue;
    let score = 0;
    let best: { s: HelpSection; n: number } | null = null;
    for (const w of words) if (title.includes(w)) score += 3;
    for (const x of secs) {
      let n = 0;
      for (const w of words) {
        if (x.head.includes(w)) n += 2;
        if (x.body.includes(w)) n += 1;
      }
      score += n;
      if (n > 0 && (!best || n > best.n)) best = { s: x.s, n };
    }
    hits.push({ article: a, section: best?.s ?? null, score });
  }
  return hits.sort((a, b) => b.score - a.score || a.article.order - b.article.order).slice(0, max);
}

/** 記事の中の言葉のキー（{customer}）と製品の言い回し（{pw:startDay}）・製品名（{product}） */
export const HELP_KEY_RE = /\{(pw:[A-Za-z]+|product|productHost|[A-Za-z]+)\}/g;
/** 知らないキー（打ちまちがい）。ビルドが止める */
export function unknownHelpKeys(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(HELP_KEY_RE)) {
    const k = m[1] ?? "";
    if (k === "product" || k === "productHost") continue;
    if (k.startsWith("pw:")) {
      if (!(k.slice(3) in PRODUCTS.genba.words)) out.push(m[0]);
      continue;
    }
    if (!(VOCAB_KEYS as readonly string[]).includes(k)) out.push(m[0]);
  }
  return out;
}

/** 製品の既定の言葉（公開のヘルプ・help:shots の見本） */
export function helpLabelsOf(product: Product): Labels {
  return PRESET_LABELS[PRODUCTS[product].vocabulary];
}
/** 公開のヘルプ：言葉のキーを製品の既定で、{pw:…}・{product} を製品で埋める */
export function fillHelpText(text: string, product: Product): string {
  return fillProduct(fillLabels(text, helpLabelsOf(product)), product);
}

/** 記事の画面が、記事の機能の束（docs/58 §10.3）に入るか。:id は見本の id にして当てる */
export function helpScreenFeature(pattern: string): ReturnType<typeof supportFeatureOf> {
  const sample = pattern.replace(/\/\*$/, "").replace(/:id\b/g, "00000000-0000-0000-0000-000000000000").replace(/:n\b/g, "1");
  return supportFeatureOf(sample || "/");
}

/** 公開のヘルプの目次の見出し（機能の束ごと。言葉のキーは製品の既定で埋める） */
export const HELP_FEATURE_LABELS: Record<SupportFeature, string> = {
  login: "ログイン・会員登録",
  settings: "自社の情報と設定",
  partner: "{customer}",
  group: "{project}",
  estimate: "見積書",
  invoice: "請求書",
  receipt: "領収書",
  payment: "入金",
  schedule: "{schedule}",
  vendor: "{vendorQuote}・見積依頼",
  paper: "紙・PDF",
  home: "ホーム",
  users: "ユーザー・会社",
  plan: "料金プラン・お支払い",
  mail: "メール",
  other: "そのほか",
};

/** 窓で数えるもの（help_views の 開いた・はい・いいえ） */
export const HelpEventKind = z.enum(["open", "yes", "no"]);
export type HelpEventKind = z.infer<typeof HelpEventKind>;
export const HelpEventRequest = z
  .object({
    article: HelpArticleId,
    feature: SupportFeature,
    kind: HelpEventKind,
  })
  .strict();
export type HelpEventRequest = z.infer<typeof HelpEventRequest>;

export const HELP_MSG = {
  /** 公開の記事があるときの「？」の窓の題（無ければ今までどおり「お問い合わせ」） */
  dialogTitle: "ヘルプ・お問い合わせ",
  top: "この画面でよく見られている質問",
  search: "ヘルプを探す",
  searchButton: "検索",
  searchNone: "見つかりませんでした",
  solved: "解決しましたか",
  yes: "はい",
  no: "いいえ",
  thanks: "ありがとうございました。",
  notSolved: (title: string) => `「${title}」を読んでも解決しなかったことを、一緒にお送りします。`,
  openFull: "ヘルプページで表示",
  back: "一覧へ",
  contents: "ヘルプの目次",
  preparing: "記事を準備しています。お困りのことはお問い合わせからお送りください。",
  draft: "下書き（まだ公開していません）",
} as const;
